import { useState, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Upload, Send, X } from "lucide-react";

const formSchema = z.object({
  staffId: z.string().min(1, "Please select a staff member"),
  customerName: z.string().min(2, "Customer name is required"),
  customerEmail: z.string().email("Please enter a valid email"),
  jobDescription: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface Staff {
  id: string;
  name: string;
}

interface ReviewRequestFormProps {
  staffMembers: Staff[];
  onSubmit: (data: FormData & { photoFile?: File }) => void;
  isSubmitting?: boolean;
}

export interface ReviewRequestFormRef {
  reset: () => void;
}

export const ReviewRequestForm = forwardRef<ReviewRequestFormRef, ReviewRequestFormProps>(
  function ReviewRequestForm({ staffMembers, onSubmit, isSubmitting }, ref) {
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      staffId: "",
      customerName: "",
      customerEmail: "",
      jobDescription: "",
    },
  });

  // Expose reset method to parent via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      form.reset();
      setPhotoFile(null);
      setPhotoPreview(null);
    },
  }));

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleSubmit = (data: FormData) => {
    onSubmit({ ...data, photoFile: photoFile || undefined });
  };

  return (
    <Card data-testid="review-request-form">
      <CardHeader>
        <CardTitle>Send Review Request</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="staffId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Staff Member</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-staff">
                        <SelectValue placeholder="Select staff member" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {staffMembers.map((staff) => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter customer name"
                      {...field}
                      data-testid="input-customer-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="customer@example.com"
                      {...field}
                      data-testid="input-customer-email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the service provided..."
                      className="resize-none"
                      rows={3}
                      {...field}
                      data-testid="input-job-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Photo upload hidden for MVP - scaffolding kept for future use
            <div>
              <Label>Job Photo (Optional)</Label>
              <div className="mt-2">
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={photoPreview}
                      alt="Job photo preview"
                      className="max-w-xs rounded-md border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2"
                      onClick={removePhoto}
                      data-testid="button-remove-photo"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-md cursor-pointer hover-elevate active-elevate-2">
                    <div className="flex flex-col items-center justify-center">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload a job photo
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      data-testid="input-job-photo"
                    />
                  </label>
                )}
              </div>
            </div>
            */}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              data-testid="button-send-request"
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? "Sending..." : "Send Review Request"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
});
